// src/admin/pages/Users.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Chip, Button, Stack, MenuItem, Select, Typography, Box, Snackbar, Alert,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import DetailDrawer from '../components/DetailDrawer';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  fetchUsers, changeUserRole, deleteUserRecord, sendUserPasswordReset, setUserSuspended, setUserEntitlement,
} from '../lib/adminApi';
import { canChangeRole, canDeleteUser, toCsv, toDate } from '../lib/adminLogic';
import { isProEntitled } from '../../lib/entitlement';

const ROLE_COLORS = { Admin: 'error', Pro: 'primary', Free: 'default' };

export default function Users() {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm }
  const [toast, setToast] = useState(null);      // { severity, msg }

  const reload = useCallback(async () => {
    try {
      setUsers(await fetchUsers());
    } catch (e) {
      // Surface permission/connection failures instead of silently showing an empty table.
      setToast({ severity: 'error', msg: `Errore nel caricamento utenti: ${e.message || e}` });
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const admins = users.filter((u) => u.role === 'Admin');
  const notify = (severity, msg) => setToast({ severity, msg });

  const onRoleChange = async (user, newRole) => {
    const check = canChangeRole({ targetId: user.id, targetRole: user.role, newRole, admins, actorUid: currentUser.uid });
    if (!check.ok) return notify('error', check.reason);
    await changeUserRole(currentUser, user, newRole);
    notify('success', `Ruolo aggiornato a ${newRole}`);
    await reload();
    setSelected((s) => (s && s.id === user.id ? { ...s, role: newRole } : s));
  };

  const onDelete = (user) => {
    const check = canDeleteUser({ targetId: user.id, targetRole: user.role, admins, actorUid: currentUser.uid });
    if (!check.ok) return notify('error', check.reason);
    setConfirm({
      title: 'Elimina utente',
      message: `Eliminare il record di ${user.email}? (Il record Firestore viene rimosso; l'account Auth resta finché non sarà gestito lato backend.)`,
      onConfirm: async () => {
        await deleteUserRecord(currentUser, user);
        setConfirm(null); setSelected(null); notify('success', 'Utente eliminato'); await reload();
      },
    });
  };

  const onReset = async (user) => {
    await sendUserPasswordReset(currentUser, user);
    notify('success', `Email di reset inviata a ${user.email}`);
  };

  // Access is entitlement-based: grant comped Pro, or reset to Free (clears
  // entitlement + subscription so the account can subscribe again).
  const onSetEntitlement = async (user, tier) => {
    await setUserEntitlement(currentUser, user, tier);
    notify('success', tier === 'Pro' ? 'Accesso Pro concesso' : 'Accesso riportato a Free (abbonamento azzerato)');
    await reload();
    const ent = tier === 'Pro'
      ? { tier: 'Pro', source: 'admin', expiresAt: null }
      : { tier: 'Free', source: null, expiresAt: null };
    setSelected((s) => (s && s.id === user.id
      ? { ...s, entitlement: ent, subscription: tier === 'Pro' ? s.subscription : null }
      : s));
  };

  const onSuspendToggle = async (user) => {
    await setUserSuspended(currentUser, user, !user.suspended);
    notify('success', user.suspended ? 'Utente riattivato' : 'Utente sospeso');
    await reload();
    setSelected((s) => (s && s.id === user.id ? { ...s, suspended: !user.suspended } : s));
  };

  const exportCsv = () => {
    const csv = toCsv(users, [
      { key: 'name', label: 'Nome' }, { key: 'email', label: 'Email' },
      { key: 'role', label: 'Ruolo' }, { key: 'id', label: 'UID' },
    ]);
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'utenti.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const columns = [
    { key: 'name', label: 'Nome', sortable: true, render: (r) => r.name || <em style={{ color: '#9aa3ab' }}>(senza nome)</em> },
    { key: 'email', label: 'Email', sortable: true, render: (r) => r.email || <em style={{ color: '#9aa3ab' }}>(nessuna email)</em> },
    { key: 'role', label: 'Ruolo', sortable: true, render: (r) => <Chip size="small" label={r.role} color={ROLE_COLORS[r.role] || 'default'} /> },
    { key: 'access', label: 'Accesso', render: (r) => {
      const pro = isProEntitled(r, new Date());
      return <Chip size="small" label={pro ? 'Pro' : 'Free'} color={pro ? 'primary' : 'default'} variant={pro ? 'filled' : 'outlined'} />;
    } },
    { key: 'createdAt', label: 'Iscritto il', render: (r) => toDate(r.createdAt)?.toLocaleDateString() || '—' },
  ];

  return (
    <>
      <PageHeader title="Utenti" subtitle={`${users.length} utenti registrati`} action={<Button variant="outlined" onClick={exportCsv}>Esporta CSV</Button>} />
      <DataTable
        columns={columns} rows={users} searchable searchKeys={['name', 'email']}
        initialSort={{ key: 'name', dir: 'asc' }}
        rowActions={(row) => <Button size="small" onClick={() => setSelected(row)}>Dettagli</Button>}
      />

      <DetailDrawer open={!!selected} title={selected?.name || selected?.email || 'Utente'} onClose={() => setSelected(null)}>
        {selected && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography>{selected.email}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">UID</Typography>
              <Typography variant="body2">{selected.id}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Ruolo</Typography>
              <Select size="small" fullWidth value={selected.role || 'Free'} onChange={(e) => onRoleChange(selected, e.target.value)}>
                <MenuItem value="Free">Free</MenuItem>
                <MenuItem value="Pro">Pro</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </Select>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Accesso effettivo</Typography>
              <Typography>
                {isProEntitled(selected, new Date())
                  ? `Pro${selected.entitlement?.source ? ` (${selected.entitlement.source})` : ''}`
                  : 'Free'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => onSetEntitlement(selected, 'Pro')}>Concedi Pro</Button>
              <Button size="small" variant="outlined" color="warning" onClick={() => onSetEntitlement(selected, 'Free')}>Riporta a Free</Button>
            </Stack>

            <Button variant="outlined" onClick={() => onReset(selected)}>Invia reset password</Button>
            <Button variant="outlined" color="warning" onClick={() => onSuspendToggle(selected)}>
              {selected.suspended ? 'Riattiva account' : 'Sospendi account'}
            </Button>
            <Button variant="contained" color="error" onClick={() => onDelete(selected)}>Elimina utente</Button>
          </Stack>
        )}
      </DetailDrawer>

      <ConfirmDialog
        open={!!confirm} title={confirm?.title} message={confirm?.message}
        confirmLabel="Elimina" onConfirm={confirm?.onConfirm} onClose={() => setConfirm(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </>
  );
}
